/**
 * clearConditions — pure, headless rule math for the Brief 013 win/timer layer.
 *
 * World (Pixi-coupled) owns the live counters + the goal gate; this module owns the
 * pure decisions so the DoD logic (condition satisfaction, the killable-enemy count
 * source, timer step conversion + expiry) is unit-testable with NO Pixi. Mirrors the
 * buffs.ts split (policy math here, side effects in World).
 *
 * Determinism: integer/`Math.round`/`Math.max` only — ESLint-clean under src/engine/
 * (no transcendentals / wall-clock). The timer is a pure step counter.
 */
import { type ClearCondition, EntityType, type LevelEntity } from './LevelFormat';

/** The live progress World feeds the satisfaction check each step. */
export interface ClearProgress {
  /** Every coin collected this life, PLACED or runtime-spawned (a QBlock reward). Feeds
   *  `reach_goal_with_coins` — bonus coins legitimately count toward its `n` (POLISH_03 #4). */
  coinsCollected: number;
  /**
   * PLACED-coin collections only (POLISH_03 #4) — excludes a QBlock/Brick runtime-spawned
   * coin. Feeds `collect_all_coins` against `totalCoins` (itself a placed-only count), so
   * that condition can't be farmed by bonking an infinite/repeatable coin block instead of
   * touching every authored coin. OPTIONAL (falls back to `coinsCollected`, the pre-fix
   * behaviour) so an existing caller that doesn't track the split counter (forbidden-to-touch
   * World.ts's HUD label path) still compiles unchanged; Sim's own win-gate (`Sim.progress`,
   * the only caller that actually GATES `cleared`) always supplies the real placed-only count.
   */
  placedCoinsCollected?: number;
  totalCoins: number;
  enemiesKilled: number;
  totalEnemies: number;
}

/** The default condition when a level omits one (legacy unconditional clear). */
export const DEFAULT_CONDITION: ClearCondition = { kind: 'reach_goal' };

/**
 * Is the active clear condition met given current progress? `reach_goal` is always
 * true (touching the flag clears). An UNKNOWN kind degrades to true (never trap a
 * player at the flag on a forward-compat level). A `defeat_all_enemies` level with
 * zero killable enemies satisfies trivially (0 >= 0) — the safe degrade when there is
 * no enemy registry yet.
 */
export function conditionSatisfied(c: ClearCondition, p: ClearProgress): boolean {
  switch (c.kind) {
    case 'reach_goal': return true;
    case 'collect_all_coins': return (p.placedCoinsCollected ?? p.coinsCollected) >= p.totalCoins;
    case 'defeat_all_enemies': return p.enemiesKilled >= p.totalEnemies;
    case 'reach_goal_with_coins': return p.coinsCollected >= (c.n ?? 0);
    default: return true;
  }
}

/**
 * Short HUD label for the active condition + progress, e.g. "Coins 3/7",
 * "Enemies 2/4", "Reach goal", "Goal +coins 3/5". Text-only (styled HUD is a later
 * brief). Pure — used by the DebugOverlay readout.
 */
export function conditionLabel(c: ClearCondition, p: ClearProgress): string {
  switch (c.kind) {
    case 'collect_all_coins': return `Coins ${p.placedCoinsCollected ?? p.coinsCollected}/${p.totalCoins}`;
    case 'defeat_all_enemies': return `Enemies ${p.enemiesKilled}/${p.totalEnemies}`;
    case 'reach_goal_with_coins': return `Goal +coins ${p.coinsCollected}/${c.n ?? 0}`;
    case 'reach_goal':
    default: return 'Reach goal';
  }
}

/**
 * The hint shown when the player reaches the flag but the condition is NOT yet met.
 * Empty for `reach_goal` (it can never be unmet). Used by World.goalHint → HUD.
 */
export function conditionHint(c: ClearCondition, p: ClearProgress): string {
  switch (c.kind) {
    case 'collect_all_coins': return `Collect all coins first (${p.placedCoinsCollected ?? p.coinsCollected}/${p.totalCoins})`;
    case 'defeat_all_enemies': return `Defeat all enemies first (${p.enemiesKilled}/${p.totalEnemies})`;
    case 'reach_goal_with_coins': return `Need ${c.n ?? 0} coins (${p.coinsCollected}/${c.n ?? 0})`;
    default: return '';
  }
}

/**
 * Enemy TYPES that route a stomp-kill through ctx.killEntity — the set World tallies
 * `enemiesKilled` against. (A spiked WALKER is un-stompable so it never reaches
 * killEntity; AMBUSH is un-stompable; neither is counted here or by isKillableEnemy.)
 */
export const KILLABLE_ENEMY_TYPES: ReadonlySet<EntityType> = new Set([
  EntityType.GOOMBA,
  EntityType.FLYER,
  EntityType.HOPPER,
  EntityType.WALKER,
  // RUN-AND-GUN (step 3). GUNNER is stompable+shootable (a plain killable). ARMORED is in the
  // set so a bullet/sword's isAttackKillable targets it + its death tallies enemiesKilled — but it
  // is EXCLUDED from isKillableEnemy below (a stomp never kills it, and a run-only archetype has
  // neither gun nor sword → gating defeat-all on it would brick that level, like a spiked WALKER).
  EntityType.GUNNER,
  EntityType.ARMORED,
  // RUN-AND-GUN step 4 — emplacements (bullet/sword hp-soak only, excluded from isKillableEnemy below).
  EntityType.TURRET,
  EntityType.SPAWNER,
  // step-4 last-2 — STRAFER + LOBBER are stompable+shootable infantry/aerial → plain killable
  // (left IN isKillableEnemy, unlike the stomp-immune ARMORED/TURRET/SPAWNER).
  EntityType.STRAFER,
  EntityType.LOBBER,
]);

/**
 * Does this placed entity count toward `defeat_all_enemies` (i.e. CAN it be defeated)?
 * Stompable enemies only — there is no other kill mechanism in this game, so an
 * un-stompable enemy (spiked WALKER, AMBUSH spider) is excluded; counting it would
 * make the condition impossible. This is the "008 killable-enemy registry" the brief
 * names, derived from type + `props.spiked`.
 */
export function isKillableEnemy(le: LevelEntity): boolean {
  if (le.type === EntityType.WALKER) return le.props?.spiked !== true; // spiny is un-stompable
  if (le.type === EntityType.ARMORED || le.type === EntityType.TURRET || le.type === EntityType.SPAWNER)
    return false; // gun-only kill → never gate defeat-all on them (like a spiked WALKER)
  return KILLABLE_ENEMY_TYPES.has(le.type); // GOOMBA / FLYER / HOPPER / GUNNER (AMBUSH excluded)
}

/** Count the COINs / killable enemies a level places — World's condition TARGETS. */
export function countCoins(entities: readonly LevelEntity[]): number {
  return entities.filter((e) => e.type === EntityType.COIN).length;
}
export function countKillableEnemies(entities: readonly LevelEntity[]): number {
  return entities.filter(isKillableEnemy).length;
}

// ── Timer (Brief 013) — pure fixed-step counter, never wall-clock ───────────────

/** Fixed sim rate (Hz) the timer counts at. The whole sim is 60 Hz. */
export const SIM_HZ = 60;

/** Author seconds → fixed sim steps. 0 / non-positive ⇒ 0 (no timer). */
export function secToSteps(sec: number | undefined): number {
  if (!sec || sec <= 0) return 0;
  return Math.round(sec * SIM_HZ);
}

/** Remaining steps given a limit + elapsed step count (clamped at 0). */
export function timeRemainingSteps(limitSteps: number, stepCount: number): number {
  return Math.max(0, limitSteps - stepCount);
}

/** Has the timer expired? (Only when a positive limit is set and it's been reached.) */
export function timerExpired(limitSteps: number, stepCount: number): boolean {
  return limitSteps > 0 && stepCount >= limitSteps;
}

/** Format a step count as MM:SS (rounding UP so "0:01" shows until truly 0). */
export function formatTime(steps: number): string {
  const totalSec = Math.ceil(Math.max(0, steps) / SIM_HZ);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}
